// Perfil metrológico inicial de Temperatura.
//
// Replica la estructura de la planilla vigente de termómetros de contacto:
// 6 puntos · 2 series · ascendente/descendente · patrón/instrumento.
// El cálculo mantiene la convención ya adoptada en el sistema:
// error del instrumento = indicación del instrumento - indicación del patrón.

const SQRT3 = Math.sqrt(3);

export const TEMPERATURE_CONTACT_TEMPLATE = {
  code: 'TEMP-CONTACT:thermometer:v2',
  revision: '2',
  family: 'temperature',
  subfamily: 'contact',
  title: 'Calibración de termómetros de contacto',
  shortTitle: 'Termómetros de contacto',
  objectFallback: 'Termómetro',
  profileKey: 'contact_thermometer',
  unit: '°C',
  unitLabel: '[°C]',
  pointCount: 6,
  method: 'Por comparación con instrumento patrón.',
  procedure: 'P-DT-PT-07-02 (Calibración de termómetros)',
};

export const TEMPERATURE_INFRARED_TEMPLATE = {
  code: 'TEMP-IR:pyrometer:v1',
  revision: '1',
  family: 'temperature',
  subfamily: 'infrared',
  title: 'Calibración de termómetros infrarrojos',
  shortTitle: 'Temperatura infrarroja',
  objectFallback: 'Termómetro infrarrojo',
  profileKey: 'infrared_thermometer',
  unit: '°C',
  unitLabel: '[°C]',
  pointCount: 6,
  method: 'Por comparación con instrumento patrón.',
  procedure: 'P-DT-PT-09-02 (Calibración de termómetros infrarrojos)',
};

export const TEMPERATURE_SENSOR_TEMPLATE = {
  code: 'TEMP-SENSOR:rtd_thermocouple:v1',
  revision: '1',
  family: 'temperature',
  subfamily: 'sensor',
  title: 'Calibración de RTD y termocuplas',
  shortTitle: 'RTD / termocuplas',
  objectFallback: 'Sensor de temperatura',
  profileKey: 'rtd_thermocouple',
  unit: '°C',
  unitLabel: '[°C]',
  pointCount: 6,
  method: 'Por comparación con instrumento patrón.',
  procedure: 'P-DT-PT-07-02 (Calibración de termómetros)',
};

export const TEMPERATURE_CHAMBER_TEMPLATE = {
  code: 'TEMP-CHAMBER:oven_chamber:v1',
  revision: '1',
  family: 'temperature',
  subfamily: 'chamber',
  title: 'Calibración de estufas y cámaras de temperatura',
  shortTitle: 'Estufas / cámaras',
  objectFallback: 'Estufa o cámara de temperatura',
  profileKey: 'temperature_chamber',
  unit: '°C',
  unitLabel: '[°C]',
  pointCount: 6,
  method: 'Por comparación con instrumento patrón.',
  procedure: 'P-DT-PT-07-02 (Calibración de cámaras de Temperatura)',
};

export const TEMPERATURE_TEMPLATES = [
  TEMPERATURE_CONTACT_TEMPLATE,
  TEMPERATURE_INFRARED_TEMPLATE,
  TEMPERATURE_SENSOR_TEMPLATE,
  TEMPERATURE_CHAMBER_TEMPLATE,
];

export function resolveTemperatureTemplate(templateCodeOrProfile) {
  const key = String(templateCodeOrProfile || '').trim();
  return TEMPERATURE_TEMPLATES.find((template) => (
    template.code === key || template.profileKey === key
  )) || TEMPERATURE_CONTACT_TEMPLATE;
}

// Valores tomados de la planilla actual. Permanecen editables por sesión para
// que el técnico use los del certificado vigente del patrón.
export const DEFAULT_TEMPERATURE_FAMILY_DATA = {
  profile_key: 'contact_thermometer',
  profile_label: 'Termómetro de contacto',
  method: TEMPERATURE_CONTACT_TEMPLATE.method,
  procedure: TEMPERATURE_CONTACT_TEMPLATE.procedure,
  tolerance_mode: 'absolute',
  standard_configuration: {},
  standard_budget: {
    drift: 0,
    other_uncertainty: 0,
    emissivity_uncertainty_c: 0,
    distance_uncertainty_c: 0,
    sensor_uncertainty_c: 0,
    cold_junction_uncertainty_c: 0,
    stability_uncertainty_c: 0,
    uniformity_uncertainty_c: 0,
  },
  infrared: {
    emissivity: 0.95,
    measurement_distance_mm: 100,
    spot_diameter_mm: null,
    target_surface: 'Cuerpo negro',
  },
  sensor: {
    sensor_type: 'PT100',
    connection_wires: '4 hilos',
    cold_junction_compensation: 'Interna',
    immersion_depth_mm: null,
  },
  chamber: {
    chamber_type: 'Estufa',
    load_condition: 'Vacía',
    stabilization_time_min: null,
    measurement_position: 'Centro geométrico',
    door_condition: 'Cerrada',
  },
  environment_readings: [
    { key: 'cycle_1_start', label: 'Inicio 1er ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'cycle_2_start', label: 'Inicio 2do ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'cycle_2_end', label: 'Fin 2do ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
};

export function defaultTemperatureFamilyData(templateCode) {
  const template = resolveTemperatureTemplate(templateCode);
  return {
    ...DEFAULT_TEMPERATURE_FAMILY_DATA,
    profile_key: template.profileKey || 'contact_thermometer',
    profile_label: template.shortTitle || template.title,
    method: template.method,
    procedure: template.procedure,
  };
}

export const TEMPERATURE_READING_FIELDS = [
  's1_asc_pattern',
  's1_asc_instrument',
  's1_desc_pattern',
  's1_desc_instrument',
  's2_asc_pattern',
  's2_asc_instrument',
  's2_desc_pattern',
  's2_desc_instrument',
];

function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function valueOr(value, fallback = 0) {
  const number = finiteOrNull(value);
  return number === null ? fallback : number;
}

function mean(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  if (!finite.length) return null;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function sampleStandardDeviation(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  if (finite.length < 2) return 0;
  const average = mean(finite);
  const variance = finite.reduce((sum, value) => sum + (value - average) ** 2, 0) / (finite.length - 1);
  return Math.sqrt(variance);
}

function rectangularFromFullWidth(value) {
  return Math.abs(valueOr(value, 0)) / (2 * SQRT3);
}

function rss(values = []) {
  return Math.sqrt(values.reduce((sum, value) => sum + valueOr(value, 0) ** 2, 0));
}

function resultAxisValue(result = {}) {
  return finiteOrNull(result.indicated_value) ?? finiteOrNull(result.reference_value);
}

function interpolate(points, value, field) {
  const finite = points
    .map((point) => ({ x: resultAxisValue(point), y: finiteOrNull(point[field]) }))
    .filter((point) => point.x !== null && point.y !== null)
    .sort((a, b) => a.x - b.x);
  if (!finite.length) return null;
  if (finite.length === 1) return finite[0].y;
  if (value < finite[0].x || value > finite[finite.length - 1].x) return null;

  const upperIndex = finite.findIndex((point) => point.x >= value);
  if (upperIndex <= 0) return finite[0].y;
  const lower = finite[upperIndex - 1];
  const upper = finite[upperIndex];
  if (upper.x === lower.x) return upper.y;
  const ratio = (value - lower.x) / (upper.x - lower.x);
  return lower.y + ratio * (upper.y - lower.y);
}

export function evaluateCertifiedTemperatureReference(value, configuration = {}) {
  const raw = finiteOrNull(value);
  const reference = configuration?.reference || {};
  const results = Array.isArray(reference.results) ? reference.results : [];
  if (raw === null) return { valid: false, reason: 'Lectura patrón vacía.' };
  if (!results.length) {
    return { valid: false, reason: 'El certificado del patrón no tiene resultados cargados.' };
  }

  const capability = reference.capability || {};
  const resolution = Math.abs(valueOr(capability.resolution, 0));
  const firstResult = results[0] || {};
  const inputMode = capability?.metadata?.reference_input_mode
    || firstResult?.metadata?.reference_input_mode
    || 'corrected_value';
  const applyCorrection = inputMode === 'raw_indication';
  const policies = new Set(results.map((result) => result.interpolation_policy || 'exact_points'));

  let correction = 0;
  if (applyCorrection) {
    const exactTolerance = Math.max(resolution / 2, 1e-9);
    const exact = results.find((result) => {
      const axis = resultAxisValue(result);
      return axis !== null && Math.abs(axis - raw) <= exactTolerance;
    });
    if (exact && finiteOrNull(exact.correction) !== null) {
      correction = finiteOrNull(exact.correction);
    } else if (policies.has('linear')) {
      correction = interpolate(results, raw, 'correction');
    } else if (policies.has('constant')) {
      correction = finiteOrNull(firstResult.correction);
    } else {
      return {
        valid: false,
        reason: 'La lectura patrón no coincide con un punto certificado y el certificado no autoriza interpolación.',
      };
    }
    if (correction === null) {
      return { valid: false, reason: 'No se pudo obtener la corrección certificada para la lectura patrón.' };
    }
  }

  const uncertaintyValues = results
    .map((result) => finiteOrNull(result.uncertainty_expanded))
    .filter((item) => item !== null);
  let uncertaintyExpanded = policies.has('linear')
    ? interpolate(results, raw, 'uncertainty_expanded')
    : null;
  if (uncertaintyExpanded === null && uncertaintyValues.length) {
    // Para puntos exactos se usa conservadoramente la mayor U declarada.
    uncertaintyExpanded = Math.max(...uncertaintyValues);
  }
  if (uncertaintyExpanded === null) {
    return { valid: false, reason: 'El certificado no informa incertidumbre para el rango utilizado.' };
  }

  const coverageFactor = valueOr(
    firstResult.coverage_factor ?? reference?.certificate?.coverage_factor,
    2,
  );
  if (coverageFactor <= 0) {
    return { valid: false, reason: 'El factor de cobertura del certificado no es válido.' };
  }

  return {
    valid: true,
    raw_value: raw,
    corrected_value: raw + correction,
    correction,
    uncertainty_expanded: Math.abs(uncertaintyExpanded),
    coverage_factor: coverageFactor,
    standard_uncertainty: Math.abs(uncertaintyExpanded) / coverageFactor,
    input_mode: inputMode,
  };
}

export function mergeTemperatureFamilyData(value = {}, templateCode = null) {
  const input = value && typeof value === 'object' ? value : {};
  const defaults = defaultTemperatureFamilyData(templateCode || input.profile_key);
  const budget = input.standard_budget && typeof input.standard_budget === 'object'
    ? input.standard_budget
    : {};
  const readings = Array.isArray(input.environment_readings)
    ? input.environment_readings
    : defaults.environment_readings;

  return {
    ...defaults,
    ...input,
    standard_budget: {
      ...defaults.standard_budget,
      ...budget,
    },
    infrared: { ...defaults.infrared, ...(input.infrared || {}) },
    sensor: { ...defaults.sensor, ...(input.sensor || {}) },
    chamber: { ...defaults.chamber, ...(input.chamber || {}) },
    environment_readings: defaults.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(readings[index] || readings.find((item) => item?.key === fallback.key) || {}),
    })),
  };
}

export function buildTemperatureBlock(templateCode) {
  const template = resolveTemperatureTemplate(templateCode);
  return {
    block_key: template.profileKey === 'contact_thermometer' ? 'temperature_contact' : 'temperature_comparison',
    display_name: `Temperatura · ${template.shortTitle || template.title}`,
    unit: template.unitLabel,
    applicable: true,
    point_count: template.pointCount,
    sort_order: 0,
    mode: 'hysteresis',
  };
}

export function buildTemperatureContactBlock() {
  return buildTemperatureBlock(TEMPERATURE_CONTACT_TEMPLATE.code);
}

export function suggestTemperatureSetpoints(rangeMin, rangeMax, count = TEMPERATURE_CONTACT_TEMPLATE.pointCount) {
  const min = finiteOrNull(rangeMin);
  const max = finiteOrNull(rangeMax);
  if (min === null || max === null || max <= min) {
    return Array.from({ length: count }, () => '');
  }
  const step = (max - min) / Math.max(1, count - 1);
  return Array.from({ length: count }, (_, index) => {
    const value = min + step * index;
    return Number(value.toFixed(4));
  });
}

export function temperatureCorrectionFullWidth({ ambientTemp, instrumentUrl } = {}) {
  const temp = finiteOrNull(ambientTemp);
  const url = finiteOrNull(instrumentUrl);
  if (temp === null || url === null || url === 0) return 0;
  // La planilla estima ±0,4 % por cada 10 K respecto de 20 °C.
  return 0.004 * (Math.abs(temp - 20) / 10) * Math.abs(url);
}

export function computeTemperaturePointMetrics(row = {}, session = {}) {
  const pattern1Asc = finiteOrNull(row.s1_asc_pattern);
  const pattern1Desc = finiteOrNull(row.s1_desc_pattern);
  const pattern2Asc = finiteOrNull(row.s2_asc_pattern);
  const pattern2Desc = finiteOrNull(row.s2_desc_pattern);
  const instrument1Asc = finiteOrNull(row.s1_asc_instrument);
  const instrument1Desc = finiteOrNull(row.s1_desc_instrument);
  const instrument2Asc = finiteOrNull(row.s2_asc_instrument);
  const instrument2Desc = finiteOrNull(row.s2_desc_instrument);

  const familyData = mergeTemperatureFamilyData(session.family_data, session.template_code);
  const rawPatternValues = [pattern1Asc, pattern1Desc, pattern2Asc, pattern2Desc].filter((value) => value !== null);
  const instrumentValues = [instrument1Asc, instrument1Desc, instrument2Asc, instrument2Desc].filter((value) => value !== null);
  if (!rawPatternValues.length || !instrumentValues.length) return null;

  const referenceEvaluations = rawPatternValues.map((value) => (
    evaluateCertifiedTemperatureReference(value, familyData.standard_configuration)
  ));
  const invalidEvaluation = referenceEvaluations.find((evaluation) => !evaluation.valid);
  if (invalidEvaluation) {
    return {
      traceability_issue: invalidEvaluation.reason,
      pattern_mean: mean(rawPatternValues),
      instrument_mean: mean(instrumentValues),
    };
  }
  const patternValues = referenceEvaluations.map((evaluation) => evaluation.corrected_value);

  const patternMean = mean(patternValues);
  const instrumentMean = mean(instrumentValues);
  const errorAbs = instrumentMean - patternMean;
  // La escala Celsius no tiene cero absoluto. El error relativo se omite cerca
  // de 0 °C, donde sería matemáticamente inestable y metrológicamente engañoso.
  const errorPct = Math.abs(patternMean) >= 1 ? (errorAbs / patternMean) * 100 : null;

  const hysteresis = Math.max(
    instrument1Asc !== null && instrument1Desc !== null ? Math.abs(instrument1Desc - instrument1Asc) : 0,
    instrument2Asc !== null && instrument2Desc !== null ? Math.abs(instrument2Desc - instrument2Asc) : 0,
  );
  const repeatability = Math.max(
    sampleStandardDeviation([instrument1Asc, instrument2Asc]),
    sampleStandardDeviation([instrument1Desc, instrument2Desc]),
  );

  const budget = familyData.standard_budget;
  const tempCorrection = temperatureCorrectionFullWidth({
    ambientTemp: session.ambient_temp_avg,
    instrumentUrl: session.instrument_range_max,
  });

  // La U del certificado es expandida: su contribución estándar es U/k.
  const uPatternCalibration = Math.max(
    ...referenceEvaluations.map((evaluation) => evaluation.standard_uncertainty),
  );
  const uPatternDrift = rectangularFromFullWidth(budget.drift);
  const referenceResolution = familyData.standard_configuration?.reference?.capability?.resolution;
  const uPatternResolution = rectangularFromFullWidth(referenceResolution);
  const uInstrumentResolution = rectangularFromFullWidth(session.instrument_resolution);
  const uInstrumentTemperature = rectangularFromFullWidth(tempCorrection);
  const uHysteresis = rectangularFromFullWidth(hysteresis);
  const uRepeatability = rectangularFromFullWidth(repeatability);
  const uOther = rectangularFromFullWidth(budget.other_uncertainty);
  const uSpecial = rss([
    rectangularFromFullWidth(budget.emissivity_uncertainty_c),
    rectangularFromFullWidth(budget.distance_uncertainty_c),
    rectangularFromFullWidth(budget.sensor_uncertainty_c),
    rectangularFromFullWidth(budget.cold_junction_uncertainty_c),
    rectangularFromFullWidth(budget.stability_uncertainty_c),
    rectangularFromFullWidth(budget.uniformity_uncertainty_c),
  ]);

  const uStandard = rss([
    uPatternCalibration,
    uPatternResolution,
  ]);
  const uCombinedOther = rss([uHysteresis, uOther, uSpecial]);
  const uc = rss([
    uStandard,
    uPatternDrift,
    uInstrumentResolution,
    uInstrumentTemperature,
    uRepeatability,
    uCombinedOther,
  ]);
  const k = 2;
  const uExpanded = uc * k;

  const tolerance = finiteOrNull(session.instrument_class);
  const toleranceMode = familyData.tolerance_mode || 'percent';
  let inTolerance = null;
  if (tolerance !== null && tolerance >= 0) {
    inTolerance = toleranceMode === 'absolute'
      ? Math.abs(errorAbs) <= tolerance
      : errorPct !== null && Math.abs(errorPct) <= tolerance;
  }

  const uncertaintyComponents = {
    pattern_calibration: uPatternCalibration,
    pattern_drift: uPatternDrift,
    pattern_resolution: uPatternResolution,
    instrument_resolution: uInstrumentResolution,
    instrument_temperature: uInstrumentTemperature,
    hysteresis: uHysteresis,
    repeatability: uRepeatability,
    other: uOther,
    special: uSpecial,
    emissivity: rectangularFromFullWidth(budget.emissivity_uncertainty_c),
    distance: rectangularFromFullWidth(budget.distance_uncertainty_c),
    sensor: rectangularFromFullWidth(budget.sensor_uncertainty_c),
    cold_junction: rectangularFromFullWidth(budget.cold_junction_uncertainty_c),
    stability: rectangularFromFullWidth(budget.stability_uncertainty_c),
    uniformity: rectangularFromFullWidth(budget.uniformity_uncertainty_c),
    temperature_correction_full_width: tempCorrection,
    reference_raw_mean: mean(rawPatternValues),
    reference_correction_mean: mean(referenceEvaluations.map((evaluation) => evaluation.correction)),
    reference_certificate_uncertainty_expanded: Math.max(
      ...referenceEvaluations.map((evaluation) => evaluation.uncertainty_expanded),
    ),
    reference_coverage_factor: Math.min(
      ...referenceEvaluations.map((evaluation) => evaluation.coverage_factor),
    ),
    reference_input_mode: referenceEvaluations[0]?.input_mode,
  };

  return {
    pattern_mean: patternMean,
    instrument_mean: instrumentMean,
    error_abs: errorAbs,
    error_pct: errorPct,
    hysteresis,
    repeatability,
    u_standard: uStandard,
    u_drift: uPatternDrift,
    u_resolution: uInstrumentResolution,
    u_repeatability: uRepeatability,
    u_temp: uInstrumentTemperature,
    u_other: uCombinedOther,
    uc,
    k,
    u_expanded: uExpanded,
    u_reported: Math.max(uExpanded, Math.abs(Number(session.instrument_resolution) || 0)), // piso por resolucion del instrumento (QA 2026-09-06)
    in_tolerance: inTolerance,
    uncertainty_components: uncertaintyComponents,
  };
}

export function computeTemperatureContactPointMetrics(row = {}, session = {}) {
  return computeTemperaturePointMetrics(row, session);
}
